import { Icon as BlueIcon } from '@blueprintjs/core';

import { useState as useHookState } from '@hookstate/core';

import { AssetContext } from '@pages/Asset/Asset.query';
import { useContext } from 'react';
import { changeContact } from '@services/queries/contact/changeContact/changeContact';
import { useMutation, useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';
import { getPermissionData } from '@utils';
import { NavigationContext } from '@layout/navigation/navigation';
import { ContactsMenu } from '@components/AssetPageMenus/ContactsMenu/ContactsMenu';
import { useSelector } from 'react-redux';
import { checkDevice } from '@store/redux/features/mobile/mobileSelectors';
import { Search } from '../Search/Search';
import { AddContacts } from './Components/AddContacts/AddContacts';
import { EditContacts } from './Components/EditContacts/EditContacts';
import { ContactInfo } from './Components/ContactInfo/ContactInfo';

import { NoContacts } from './Components/NoContacts/NoContacts';

import {
  Container,
  Title,
  Head,
  Row,
  Item,
  ColumnWrapper,
  RowWrapper,
  LineDivider,
  FavoriteButton,
  Items,
  Chevron,
  IconButton,
  Show,
  CounterWrapper,
  Counter,
} from './Contacts.styles';
import { ContactsLoading, CountLoading } from './Contacts.loading';
import {
  makeShortcutRefCallback,
  shortcuthashes,
} from '../Sidebar/components/Shortcuts/Shortcuts.utils';
import { NoFeatureMenu } from '../Updates/Components/NoFeatureMenu/NoFeatureMenu';

export const Contacts = () => {
  const {
    equipmentQuery,
    openAddContact,
    contactsQuery,
    selectedContactId,
    openEditContact,
    sortContactByFavorite,
    shortcutRefs,
  } = useContext(AssetContext);
  const { role } = useContext(NavigationContext);

  const equipment = equipmentQuery?.data;

  const params = useParams<{ equipmentId: string }>();

  const search = useHookState<string>('');

  const queryClient = useQueryClient();

  const changeMutation = useMutation(changeContact, {
    onSuccess: () => {
      queryClient.invalidateQueries([
        'equipments',
        params.equipmentId,
        'contacts',
      ]);
    },
  });

  const favoritePermission = getPermissionData({
    owner: true,
    action: 'favorite',
    role,
    type: 'Contact',
  });
  const { isDesktop, isMobile } = useSelector(checkDevice());

  if (
    !contactsQuery ||
    !contactsQuery.data ||
    contactsQuery.isLoading ||
    contactsQuery.isError
  ) {
    return <ContactsLoading />;
  }

  const selectedContact = contactsQuery.data.find(
    c => c.id === selectedContactId.get(),
  );

  const count = () => {
    return contactsQuery?.data?.length || 0;
  };

  const isCountLoading = () => {
    return contactsQuery?.isLoading;
  };

  const isEmpty = contactsQuery.data.length === 0;
  const refCallback = makeShortcutRefCallback(
    shortcutRefs,
    shortcuthashes.contacts,
  );
  return (
    <Container ref={refCallback}>
      {selectedContact && <ContactInfo contact={selectedContact} />}

      {(!selectedContact || isEmpty) && (
        <Head>
          <CounterWrapper>
            <Title>Contacts</Title>
            {isCountLoading() ? <CountLoading /> : <Counter>{count()}</Counter>}
          </CounterWrapper>
          <Row>
            {isDesktop && (
              <Search loadSearch={search.set} helperText="Find a contact" />
            )}
            <IconButton>
              {isDesktop && <ContactsMenu />}
              {isMobile && <NoFeatureMenu />}
            </IconButton>
          </Row>
        </Head>
      )}

      <Show show={!selectedContact}>
        <Items>
          {contactsQuery.data
            .filter(item =>
              item.name?.toLowerCase().includes(search.get().toLowerCase()),
            )
            .sort((a, b) => {
              if (sortContactByFavorite.get()) {
                return Number(b.favorite) - Number(a.favorite);
              }
              return 0;
            })
            .map((contact, idx) => (
              <div key={contact.id}>
                {idx !== 0 && <LineDivider />}
                <Item>
                  <FavoriteButton
                    center={!contact.title}
                    onClick={() => {
                      if (favoritePermission.allowed) {
                        changeMutation.mutate({
                          data: {
                            contactId: contact.id,
                            favorite: !contact.favorite,
                          },
                        });
                      }
                    }}
                  >
                    {contact.favorite ? (
                      <BlueIcon icon="star" color="#ffc979" />
                    ) : (
                      <BlueIcon icon="star-empty" color="#8F95B2" />
                    )}
                  </FavoriteButton>
                  <ColumnWrapper
                    onClick={() => selectedContactId.set(contact.id)}
                  >
                    <RowWrapper>
                      <Title className="itemHeavy">{contact.name}</Title>
                      {contact.title && (
                        <Title className="itemLight">{contact.title}</Title>
                      )}
                    </RowWrapper>

                    <Chevron>
                      <BlueIcon icon="chevron-right" color="#8F95B2" />
                    </Chevron>
                  </ColumnWrapper>
                </Item>
              </div>
            ))}
        </Items>
      </Show>

      {isEmpty && <NoContacts />}
      {openAddContact.get() && (
        <AddContacts
          manufacturerData={equipment?.product?.manufacturer}
          openAddContact={openAddContact}
        />
      )}

      {selectedContact && openEditContact.get() && (
        <EditContacts
          manufacturerData={equipment?.product?.manufacturer}
          openEditContact={openEditContact}
          contact={selectedContact}
        />
      )}
    </Container>
  );
};
